/**
 * Targeted PET check-in invites — queue SMS via the shared `messages` collection
 * and soft-link metadata under `clinics/{clinicId}/checkInInvites`.
 *
 * MULTI-FACILITY TODO: When org-scoped clinics and PETs ship, pass facility routing
 * (e.g. facilityRootDocId or per-facility Twilio sender config) in addition to `clinicId`,
 * and resolve the outbound number from org settings. `origin` remains caller-supplied so
 * this package does not import from unified-app.
 *
 * MULTI-FACILITY TODO (checkInInvites): Invites are stored under the clinic doc used for PET
 * delivery today. If a patient belongs to a different facility root than the template’s
 * clinic, or if outbound SMS should be attributed per org hub, mirror or relocate this doc
 * so analytics and patient self-read rules stay consistent.
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';
import { normalizePhoneNumber } from '@healthdesk/shared-utils';

/**
 * Queue an SMS with the targeted check-in join link (`/join/t_<inviteCode>`).
 *
 * @param {Object} params
 * @param {string} params.clinicId
 * @param {string} params.templateId - Check-in template document id
 * @param {string} params.inviteCode - Raw PET invite code (without `t_` prefix)
 * @param {string} [params.templateName] - Used in default SMS body
 * @param {string} params.toPhone - Recipient phone (normalized before write)
 * @param {string} params.sentBy - Staff user uid
 * @param {string} [params.patientUid] - Optional linked patient uid
 * @param {string} [params.patientDisplayName] - Optional display name for invite metadata
 * @param {string} [params.customMessage] - When non-empty after trim, replaces the default SMS body entirely
 * @param {string} params.origin - Web app base URL (e.g. https://app.example.com), no trailing slash required
 * @returns {Promise<{ messageId: string, joinUrl: string, inviteId: string }>}
 */
export async function sendTargetedCheckInLink(params) {
  const {
    clinicId,
    templateId,
    inviteCode,
    templateName,
    toPhone,
    sentBy,
    patientUid = null,
    patientDisplayName,
    customMessage,
    origin,
  } = params || {};

  if (!clinicId || !templateId || !inviteCode || !toPhone || !sentBy || !origin) {
    throw new Error(
      'sendTargetedCheckInLink: missing required fields (clinicId, templateId, inviteCode, toPhone, sentBy, origin)'
    );
  }

  const base = String(origin).replace(/\/+$/, '');
  const code = String(inviteCode).trim();
  const joinUrl = `${base}/join/t_${code}`;

  const defaultBody =
    templateName && String(templateName).trim()
      ? `HealthDesk: complete your check-in for ${String(templateName).trim()}: ${joinUrl}`
      : `HealthDesk: complete your check-in: ${joinUrl}`;

  const trimmedCustom = customMessage != null ? String(customMessage).trim() : '';
  const body = trimmedCustom ? trimmedCustom : defaultBody;

  const to = normalizePhoneNumber(toPhone);

  if (process.env.REACT_APP_TWILIO_ENABLED !== 'true') {
    console.warn(
      '[targetedCheckInService] REACT_APP_TWILIO_ENABLED is not "true"; message document was queued but SMS delivery depends on Twilio/backend configuration.'
    );
  }

  const messageRef = db.collection('messages').doc();
  const inviteRef = db.collection('clinics').doc(clinicId).collection('checkInInvites').doc();

  const trimmedTemplateName = templateName != null && String(templateName).trim() ? String(templateName).trim() : null;
  const trimmedPatientName =
    patientDisplayName != null && String(patientDisplayName).trim()
      ? String(patientDisplayName).trim()
      : null;

  const batch = db.batch();

  batch.set(messageRef, {
    to,
    body,
    type: 'targeted_checkin_invite',
    clinicId,
    templateId,
    patientUid: patientUid ?? null,
    sentBy,
    joinUrl,
    status: 'pending',
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

  batch.set(inviteRef, {
    inviteId: inviteRef.id,
    clinicId,
    templateId,
    templateName: trimmedTemplateName,
    patientUid: patientUid ?? null,
    patientDisplayName: trimmedPatientName,
    toPhone: to,
    joinUrl,
    sentBy,
    sentAt: firebase.firestore.FieldValue.serverTimestamp(),
    status: 'sent',
    visitId: null,
  });

  await batch.commit();

  return { messageId: messageRef.id, joinUrl, inviteId: inviteRef.id };
}

/**
 * Mark the newest matching targeted invite as completed when the patient finishes check-in.
 *
 * MULTI-FACILITY TODO: If invites move under org scope or are deduped across clinics,
 * adjust this query path and composite indexes accordingly.
 *
 * @param {string} clinicId
 * @param {string} templateId
 * @param {string} patientUid
 * @param {string} visitId
 * @returns {Promise<void>}
 */
export async function updateInviteStatus(clinicId, templateId, patientUid, visitId) {
  if (!clinicId || !templateId || !patientUid || !visitId) {
    throw new Error('updateInviteStatus: clinicId, templateId, patientUid, and visitId are required');
  }

  const snap = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInInvites')
    .where('templateId', '==', templateId)
    .where('patientUid', '==', patientUid)
    .where('status', '==', 'sent')
    .limit(1)
    .get();

  if (snap.empty) {
    return;
  }

  await snap.docs[0].ref.update({
    status: 'completed',
    visitId,
    completedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}
